import type { MarketType } from '@/utils/types'
import { ref, type Ref } from 'vue'
import { useMyToast } from './useMyToast'
import { i18n } from '@/i18n'
import { useTrade, type Severities } from './useTrade'
import type { MarginMode } from '@/services/types'
import { isUndefined } from 'lodash'
import TriggerButton from '@/components/TriggerButton.vue'
import { sleep, getBuyMarketType, getSellMarketType } from '@/utils'
import { buildSymbol, DEFAULT_QUOTE } from '@/utils/quoteHelpers'
import type { ApiKeyType } from '@/services/api-keys-v2-service/types'
import { useCoreStore } from '@/stores/coreStore'

interface CloseActionParams {
  amount: number
  side: 'buy' | 'sell'
  price: number
  profileNum: number
}

export const useCexFeatures = (
  // cacheKeyPostfix: Ref<string>,
  orderNotifications: Ref<boolean>,
  enabledProfiles: Ref<number[]>,
  profilesMax: Ref<number | null>,
  apiKeyType: ApiKeyType = 'cex_cex',
) => {
  const { addToast } = useMyToast()

  const coreStore = useCoreStore()
  const { t } = i18n.global
  const creatingOrder = ref(false)
  const preparingProfiles = ref(false)
  const preparingProfile = ref(false)
  const creatingOrders = ref(false)
  const closingPositions = ref(false)
  const cancelingOrders = ref(false)
  const fetchingBalance = ref(false)
  const isProfilesPrepared = ref(false)
  // const { enabledProfiles, profilesMax } = useProfiles(cacheKeyPostfix, apiKeyType)

  const {
    fetchBalance,
    createOrderPro,
    prepareSingleProfile,
    createOrders,
    prepareProfile,
    cancelOrders,
    closePositions,
  } = useTrade(profilesMax, enabledProfiles, orderNotifications)

  // Подготовка профилей
  const prepareProfilesWrapper = async (
    asset: string,
    exBuy: string,
    exSell: string,
    leverage: number,
    marginMode: MarginMode,
    marketType: MarketType,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
  ) => {
    if (!asset || !exBuy || !exSell || !marketType) {
      return
    }

    isProfilesPrepared.value = true

    if (!leverage) {
      addToast({
        severity: 'error',
        message: t('invalidLeverage'),
      })
      return
    }
    preparingProfiles.value = true
    const promises = [] as Promise<void>[]
    enabledProfiles.value.forEach((x) => {
      promises.push(
        prepareProfile(
          asset as string,
          exBuy as string,
          exSell as string,
          marketType as MarketType,
          leverage,
          marginMode,
          x,
          sellAsset,
          quoteBuy,
          quoteSell,
        ),
      )
    })
    try {
      await Promise.allSettled(promises)
    } finally {
      preparingProfiles.value = false
      isProfilesPrepared.value = true
    }
  }

  const prepareProfileWrapper = async (
    cex: string,
    asset: string,
    leverage: number,
    quote: string = DEFAULT_QUOTE,
  ) => {
    preparingProfile.value = true
    const tasks = [] as Promise<void>[]
    enabledProfiles.value.forEach((x) => {
      tasks.push(prepareSingleProfile(cex, asset, x, apiKeyType, leverage ?? 1, 'isolated', quote))
    })
    try {
      await Promise.allSettled(tasks)
    } finally {
      preparingProfile.value = false
      isProfilesPrepared.value = true
    }
  }

  const createReduceOnlyOrderWrapper = async (
    cex: string,
    asset: string,
    priceBuy: number,
    priceSell: number,
    quote: string = 'USDT',
  ) => {
    creatingOrder.value = true
    const actionsParams = [] as CloseActionParams[]
    const symbol = buildSymbol(asset, quote, 'swap')
    enabledProfiles.value.forEach((userProfile) => {
      if (!coreStore.positions[userProfile]) {
        console.log(`no poses for #${userProfile + 1} found`)
        return
      }

      const pz = coreStore.positions[userProfile][cex]
      if (!pz) {
        return
      }

      const openPositions = pz.filter(
        // eslint-disable-next-line
        (x: any) => x['symbol'] == symbol,
      )
      // eslint-disable-next-line
      openPositions.forEach((x: any) => {
        const amount = Number((x['contractSize'] ?? 1) * x['contracts'])
        const newSide = x['side'] == 'long' ? 'sell' : 'buy'
        const price = newSide == 'buy' ? priceBuy : priceSell
        if (!price) {
          return
        }
        actionsParams.push({
          profileNum: userProfile,
          amount: amount,
          side: newSide,
          price: price,
        })
      })
    })

    const tasks = [] as Promise<Severities>[]
    actionsParams.forEach((x) => {
      tasks.push(
        createOrderPro(
          cex,
          asset,
          x.amount,
          1,
          x.profileNum,
          x.price,
          x.side,
          'swap',
          true,
          true,
          'isolated',
          apiKeyType,
        ),
      )
    })

    window.setTimeout(() => {
      creatingOrder.value = false
    }, 2000)
    const ret = await Promise.allSettled(tasks)
    const anybodySuccessed =
      ret.filter((x) => x.status === 'fulfilled' && x.value == 'success').length > 0
    return anybodySuccessed
  }

  const createOrderWrapper = async (
    cex: string,
    asset: string,
    side: 'buy' | 'sell',
    amount: number,
    price: number,
    leverage: number,
    reduceOnly: boolean,
    quote: string = DEFAULT_QUOTE,
  ) => {
    if (creatingOrder.value) {
      return
    }
    creatingOrder.value = true
    const promises = [] as Promise<Severities>[]
    enabledProfiles.value.forEach((x) => {
      promises.push(
        createOrderPro(
          cex,
          asset,
          amount,
          leverage,
          x,
          price,
          side,
          'swap',
          reduceOnly,
          false,
          'isolated',
          apiKeyType,
          quote,
        ),
      )
    })
    window.setTimeout(() => {
      creatingOrder.value = false
    }, 3000)
    const ret = await Promise.allSettled(promises)
    const successed = ret.filter((x) => x.status === 'fulfilled' && x.value == 'success').length
    return successed
  }

  // Создание заявок
  const createOrdersWrapper = async (
    asset: string,
    leverage: number,
    exBuy1: string,
    exSell1: string,
    amount: number,
    slippage: number,
    reduceOnly: boolean,
    priceBuy1: number,
    priceSell1: number,
    backPriceBuy: number,
    backPriceSell: number,
    marketType: MarketType,
    marginMode: MarginMode,
    closeOnFail: boolean,
    autoPrepare: boolean,
    useMarketOrder: boolean,
    triggerValue: Ref<number | null>,
    triggerButton: Ref<InstanceType<typeof TriggerButton> | null>,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
  ) => {
    if (!exBuy1 || !exSell1 || !asset || !marketType) {
      return
    }
    if (
      (!reduceOnly && (!priceBuy1 || !priceSell1)) ||
      (reduceOnly && (!backPriceBuy || !backPriceSell))
    ) {
      addToast({
        severity: 'error',
        message: t('modal.createSwapOrders.msgs.noVol'),
      })
      return
    }

    if (creatingOrders.value) {
      return
    }

    const oldTriggerValue = triggerValue.value
    triggerValue.value = null
    if (!isUndefined(triggerButton?.value?.visibleTriggerValue)) {
      triggerButton.value.visibleTriggerValue = oldTriggerValue
    }

    let priceBuy = null as number | null
    let priceSell = null as number | null
    let exBuy = null as string | null
    let exSell = null as string | null
    let reverse = false
    let orderAsset = asset
    let orderSellAsset = sellAsset
    let orderQuoteBuy = quoteBuy
    let orderQuoteSell = quoteSell

    if (reduceOnly) {
      if (marketType == 'spot-swap') {
        reverse = true
        priceBuy = backPriceBuy
        priceSell = backPriceSell
        exBuy = exBuy1
        exSell = exSell1
      } else {
        priceBuy = backPriceSell
        priceSell = backPriceBuy
        exBuy = exSell1
        exSell = exBuy1
      }
      orderAsset = sellAsset || asset
      orderSellAsset = asset
      orderQuoteBuy = quoteSell
      orderQuoteSell = quoteBuy
    } else {
      priceBuy = priceBuy1
      priceSell = priceSell1
      exBuy = exBuy1
      exSell = exSell1
    }

    creatingOrders.value = true
    if (autoPrepare && !isProfilesPrepared.value) {
      await prepareProfilesWrapper(
        asset,
        exBuy,
        exSell,
        leverage,
        marginMode,
        marketType,
        sellAsset,
      )
    }

    if (orderNotifications.value) {
      addToast({
        severity: 'success',
        message: t('placingOrders'),
      })
    }

    const promises = [] as Promise<void>[]

    enabledProfiles.value.forEach((x) => {
      if (!orderAsset || !marketType || !amount) {
        return
      }
      promises.push(
        createOrders(
          orderAsset,
          marketType,
          amount,
          slippage,
          leverage,
          useMarketOrder,
          reduceOnly,
          reverse,
          priceBuy,
          priceSell,
          exBuy,
          exSell,
          x,
          marginMode ?? 'isolated',
          closeOnFail,
          orderSellAsset,
          orderQuoteBuy,
          orderQuoteSell,
        ),
      )
    })
    await sleep(2000)
    // await Promise.allSettled(promises)
    creatingOrders.value = false
  }

  const fetchBalanceWrapper = async (cex: string) => {
    fetchingBalance.value = true
    const promises = [] as Promise<void>[]
    enabledProfiles.value.forEach((x) => {
      promises.push(fetchBalance(cex as string, 'swap', x, apiKeyType))
    })
    await Promise.allSettled(promises)
    fetchingBalance.value = false
  }

  // Узнать баланс
  const fetchBalancesWrapper = async (
    exBuy: string,
    exSell: string,
    marketType: MarketType,
    quoteBuy?: string,
    quoteSell?: string,
  ) => {
    fetchingBalance.value = true
    const promises = [] as Promise<void>[]

    const exBuyMarketType = getBuyMarketType(marketType)
    const exSellMarketType = getSellMarketType(marketType)

    enabledProfiles.value.forEach((x) => {
      promises.push(fetchBalance(exBuy as string, exBuyMarketType, x, 'cex_cex', quoteBuy))
      promises.push(fetchBalance(exSell as string, exSellMarketType, x, 'cex_cex', quoteSell))
    })
    await Promise.allSettled(promises)
    fetchingBalance.value = false
  }

  // Быстрое закрытие позиций
  const closePositionsWrapper = async (
    exBuy: string,
    exSell: string,
    asset: string,
    marketType: MarketType,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
    priceBuy?: number,
    priceSell?: number,
  ) => {
    closingPositions.value = true
    const promises = [] as Promise<void>[]
    enabledProfiles.value.forEach((x) => {
      promises.push(
        closePositions(
          exBuy as string,
          exSell as string,
          marketType as MarketType,
          asset as string,
          x,
          sellAsset,
          quoteBuy,
          quoteSell,
          priceBuy,
          priceSell,
        ),
      )
    })
    await Promise.allSettled(promises)
    closingPositions.value = false
  }

  // Отмена заявок
  const cancelOrdersWrapper = async (
    exBuy: string,
    exSell: string,
    asset: string,
    marketType: MarketType,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
  ) => {
    cancelingOrders.value = true
    const promises = [] as Promise<void>[]
    enabledProfiles.value.forEach((x) => {
      promises.push(
        cancelOrders(
          exBuy as string,
          exSell as string,
          marketType as MarketType,
          asset as string,
          x,
          sellAsset,
          quoteBuy,
          quoteSell,
        ),
      )
    })
    await Promise.allSettled(promises)
    cancelingOrders.value = false
  }

  return {
    creatingOrders,
    creatingOrder,
    cancelingOrders,
    closingPositions,
    fetchingBalance,
    preparingProfiles,
    isProfilesPrepared,
    preparingProfile,
    prepareProfilesWrapper,
    createOrdersWrapper,
    cancelOrdersWrapper,
    closePositionsWrapper,
    prepareProfileWrapper,
    fetchBalancesWrapper,
    fetchBalanceWrapper,
    createOrderWrapper,
    createReduceOnlyOrderWrapper,
  }
}
